import {
  type TransactionDetailDto,
  TransactionStatus as XeTransactionStatus,
  TransactionType,
  VoucherType,
} from "Api/Api";
import { Colors } from "Components/Layout/Themes/Colors";
import {
  PaymentInstructionStatus,
  TransactionDirection,
  TransactionStatus,
} from "Infrastructure/Api/Api";
import { Resources } from "Translations/Resources";

type ResourceType = (resourcePath: string, options?: any) => string;

const _TransactionResources = Resources.Transactions;

/** Length of transaction public ID (e.g., "ABC123") */
export const TRANSACTION_PUBLIC_ID_LENGTH = 6;

/**
 * Characters a transaction public ID can contain, mirroring the backend
 * TransactionPublicIDGenerator: no I/O/0/1, so codes stay unambiguous on paper.
 * Keep in sync if the generator's alphabet ever changes.
 */
export const TRANSACTION_PUBLIC_ID_PATTERN =
  /^[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]+$/;

export function getTypeName(t: ResourceType, item: TransactionDetailDto) {
  switch (item.type) {
    case TransactionType.Sell:
      return t(Resources.Transactions.Type.Sell);
    case TransactionType.Withdrawal:
      return t(Resources.Transactions.Type.Withdrawal);
    case TransactionType.LeadReward:
      return t(Resources.Transactions.Type.LeadReward);
    case TransactionType.LeadRewardConsumer:
      return t(Resources.Transactions.Type.LeadRewardConsumer);
    case TransactionType.Voucher:
      return item.voucherType === VoucherType.SignUpSubsidy
        ? t(Resources.Transactions.Type.Voucher.SignUpSubsidy)
        : t(Resources.Transactions.Type.Voucher.GiftCard);
    case TransactionType.Coinback:
      return t(Resources.Transactions.Type.Coinback);
    default:
      return t(Resources.Transactions.Type.Buy);
  }
}

export const getStatusName = (t: ResourceType, item: TransactionDetailDto) => {
  switch (item.status) {
    case XeTransactionStatus.Completed:
      return t(Resources.Transactions.Status.Completed);
    case XeTransactionStatus.WithdrawRequested:
      return t(Resources.Transactions.Status.WithdrawRequested);
    case XeTransactionStatus.Withdrawn:
      return t(Resources.Transactions.Status.Withdrawn);
    case XeTransactionStatus.PendingPayout:
      return t(Resources.Transactions.Status.PendingPayout);
    default:
      return t(Resources.Transactions.Status.PendingTrade);
  }
};

// Direction of a transaction from the current user's perspective, derived
// client-side from creditor/debtor — unlike the full API enum, it can never
// be a refund variant, so lookup maps over it stay exhaustive at 3 entries.
export type ViewerTransactionDirection =
  | TransactionDirection.Incoming
  | TransactionDirection.Outgoing
  | TransactionDirection.Unknown;

export const getTransactionDirection = (
  { creditorID, debtorID }: { creditorID: number; debtorID?: number | null },
  userID: number,
): ViewerTransactionDirection => {
  if (userID === creditorID) {
    return TransactionDirection.Incoming;
  }

  if (userID === debtorID) {
    return TransactionDirection.Outgoing;
  }

  return TransactionDirection.Unknown;
};

export const paymentInstructionStatusToColor: Record<
  PaymentInstructionStatus,
  string
> = {
  [PaymentInstructionStatus.None]: Colors.WarningDark,
  [PaymentInstructionStatus.AcceptedCustomerProfile]: Colors.GreenDark,
  [PaymentInstructionStatus.AcceptedSettlementCompleted]: Colors.GreenDark,
  [PaymentInstructionStatus.AcceptedTechnicalValidation]: Colors.WarningDark,
  [PaymentInstructionStatus.Rejected]: Colors.RedDark,
  [PaymentInstructionStatus.AcceptedWithChange]: Colors.WarningDark,
};

export const transactionStatusToColor: Record<TransactionStatus, string> = {
  [TransactionStatus.Created]: Colors.WarningDark,
  [TransactionStatus.Initiated]: Colors.WarningDark,
  [TransactionStatus.Processing]: Colors.WarningDark,
  [TransactionStatus.Success]: Colors.GreenDark,
  [TransactionStatus.Fail]: Colors.RedDark,
  [TransactionStatus.Cancelled]: Colors.RedDark,
  [TransactionStatus.InvestigationNeeded]: Colors.RedDark,
};

export type TransactionRefundState = "refunded" | "refundPending";

export const refundStateToLabelKey: Record<TransactionRefundState, string> = {
  refunded: Resources.Transactions.RefundState.Refunded,
  refundPending: Resources.Transactions.RefundState.Pending,
};

export const refundStateToColor: Record<TransactionRefundState, string> = {
  refunded: Colors.RedDark,
  refundPending: Colors.WarningDark,
};

/**
 * Derived refund state of a PAID original (ZS-1676): the original stays Success after a refund —
 * whether the money went back is carried by the linked refund row's status (the backend sends
 * relatedTransactionStatus only for Type=Refund counterparts). Refund rows themselves (isRefund)
 * and unpaid/failed transactions have no refund state; a dead refund (Fail/Cancelled — the money
 * never moved) means no state either, while anything else (including InvestigationNeeded, a refund
 * under manual review) is pending. Mirrors TransactionTextHelper.GetStatusText on the backend.
 */
export const getTransactionRefundState = (transaction: {
  status?: TransactionStatus;
  isRefund?: boolean;
  relatedTransactionStatus?: TransactionStatus | null;
}): TransactionRefundState | null => {
  const { status, isRefund, relatedTransactionStatus } = transaction;

  if (isRefund || status !== TransactionStatus.Success) {
    return null;
  }

  if (!relatedTransactionStatus) {
    return null;
  }

  if (relatedTransactionStatus === TransactionStatus.Success) {
    return "refunded";
  }

  if (
    relatedTransactionStatus === TransactionStatus.Fail ||
    relatedTransactionStatus === TransactionStatus.Cancelled
  ) {
    return null;
  }

  return "refundPending";
};
